import {
    EateryAudience,
    EateryDecor,
    EateryDomain,
    EateryEvent,
    EateryGroup,
} from 'api'
import { createRoot, onCleanup } from 'solid-js'
import { createStore } from 'solid-js/store'
import { addAlert } from './user'
import { eatery_state } from './eatery'
import { deepcopy, getDistance } from 'shared'
import { search } from './search'

export const IRAN_MAX_BOUNDS = [26, 13, 80, 47]
export const MAP_ZOOM_STEP = 0.5
export const MAP_EASE_DURATION = 200

type TOOLTIP = {
    show: boolean
    coords: {
        lat: number
        lng: number
    }
    pos: {
        x: number
        y: number
    }
}
type FILTERS = {
    show: boolean
    event: EateryEvent | null
    decor: EateryDecor | null
    group: EateryGroup | null
    domain: EateryDomain | null
    audience: EateryAudience | null
}
type STATE = {
    isCentering: boolean
    isActive: boolean
    loading: boolean
    // is_3d: boolean
    filters: FILTERS
    event: boolean

    showDiscountInventory: boolean

    tooltip: TOOLTIP
}
const FILTERS_DEF: FILTERS = {
    show: false,
    event: null,
    decor: null,
    audience: null,
    group: null,
    domain: null,
}

export const MAP_STATE_DEF: STATE = {
    isCentering: false,
    isActive: false,
    loading: true,
    // is_3d: false,
    filters: deepcopy(FILTERS_DEF),
    event: false,

    showDiscountInventory: false,

    tooltip: {
        show: false,
        pos: {
            x: -1,
            y: -1,
        },
        coords: {
            lat: -1,
            lng: -1,
        },
    },
}

const [mapState, setMapState] = createStore<STATE>(deepcopy(MAP_STATE_DEF))

type locationType = {
    userLat: number
    userLng: number
    watchId: number | null
    alerted: boolean
}
const [location, setLocation] = createStore<locationType>({
    userLat: 0,
    userLng: 0,
    watchId: null,
    alerted: false,
})

async function startLiveLocationTracking(_msg: boolean = false): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        if (location.watchId) {
            resolve()
            return
        }

        if (!navigator.geolocation) return reject('no geolocation')

        // Start live tracking
        const watchId = navigator.geolocation.watchPosition(
            position => {
                const { latitude, longitude } = position.coords

                setLocation({
                    userLat: latitude,
                    userLng: longitude,
                    watchId,
                })

                resolve()
            },
            error => {
                setLocation({
                    ...location,
                    watchId: null,
                })

                reject('failed to watch pos: ' + error)

                if (location.alerted) return

                addAlert({
                    subject: 'لوکشین پیدا نشد',
                    timeout: 3,
                    type: 'info',
                })
                setLocation('alerted', true)
            },
            { enableHighAccuracy: true }
        )

        setLocation({ ...location, watchId })
    })
}

export const goToUserLocation = (
    map: maplibregl.Map | undefined,
    lat: number | null | undefined,
    lng: number | null | undefined,
    onFail: () => void = () => {},
    onLoading: () => void = () => {},
    onSuccess: () => void = () => {},
    zoom = 16.5
): void => {
    if (!map) return

    if (!lat || !lng) return onFail()

    if (!isInCountry(lat, lng, true)) {
        return onFail()
    }

    onLoading()

    flyToWithCallback(
        [lng, lat],
        () => {
            onSuccess()
        },
        zoom
    )
}

export function mapSetFilter(
    kind: Exclude<keyof FILTERS, 'show'>,
    value:
        | EateryEvent
        | EateryDecor
        | EateryAudience
        | EateryGroup
        | EateryDomain
        | null
) {
    setMapState('filters', {
        event: kind === 'event' ? (value as EateryEvent) : null,
        decor: kind === 'decor' ? (value as EateryDecor) : null,
        audience: kind === 'audience' ? (value as EateryAudience) : null,
        group: kind === 'group' ? (value as EateryGroup) : null,
        domain: kind === 'domain' ? (value as EateryDomain) : null,
    })
}
export function mapClearFilters(show = false) {
    setMapState('filters', {
        ...deepcopy(FILTERS_DEF),
        show,
    })
}
export function mapFiltersToggleShow(show: boolean) {
    setMapState('filters', { show })
}

export const isInCountry = (
    lat: number,
    lng: number,
    alert?: boolean
): boolean => {
    let inside = true

    const [west, south, east, north] = IRAN_MAX_BOUNDS as number[]

    if (!west || !south || !east || !north) {
        console.warn('iran bounds is not correct')
        return true
    }

    inside = lng >= west && lng <= east && lat >= south && lat <= north

    if (!inside && alert) {
        addAlert({
            type: 'error',
            subject: 'لوکشین خارج از ایرانه',
            timeout: 3,
        })
    }

    return inside
}

export let map: maplibregl.Map | undefined
export const setMapRef = (e: maplibregl.Map) => (map = e)
export const clearMapRef = () => (map = undefined)

export function placeMarker(fly = false) {
    if (!map || !eatery_state.eatery.gene) return

    if (fly) {
        flyToWithCallback(
            [
                eatery_state.eatery.location.longitude,
                eatery_state.eatery.location.latitude,
            ],
            () => {},
            16
        )
    }

    const source = map.getSource('selected-eatery') as maplibregl.GeoJSONSource
    if (!source) return

    source.setData({
        type: 'FeatureCollection',
        features: [
            {
                type: 'Feature',
                properties: {
                    gene: eatery_state.eatery.gene,
                    cg: eatery_state.eatery.compact_gene,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [
                        eatery_state.eatery.location.longitude,
                        eatery_state.eatery.location.latitude,
                    ],
                },
            },
        ],
    })

    map.setLayoutProperty('selected-eatery-symbol', 'visibility', 'visible')
}
export function removeEateryMarker() {
    if (!map) return

    const source = map.getSource('selected-eatery') as maplibregl.GeoJSONSource
    if (!source) return

    source.setData({
        type: 'FeatureCollection',
        features: [],
    })

    map.setLayoutProperty('selected-eatery-symbol', 'visibility', 'none')
}

export const getLastMapZoom = () => {
    return LAST_MAP_INFO?.zoom || null
}
export const getLastMapCenter = () => {
    let c = LAST_MAP_INFO

    if (!c) return null

    const lat = c.lat
    const lng = c.lng

    if (!isInCountry(lat, lng)) return null

    return {
        lat,
        lng,
    }
}
export const saveMapData = () => {
    if (!map) return

    let c = map.getCenter()
    let z = map.getZoom()

    let zoom = Math.min(z, 15)

    if (!isInCountry(c.lat, c.lng)) {
        globalThis.LAST_MAP_INFO = null
        return
    }

    globalThis.LAST_MAP_INFO = {
        zoom,
        lat: c.lat,
        lng: c.lng,
    }
    return
}

const stopLiveLocationTracking = () => {
    if (location.watchId !== null) {
        navigator.geolocation.clearWatch(location.watchId)
        setLocation({ watchId: null })
    }
}

export function flyToWithCallback(
    target: maplibregl.LngLatLike,
    callback: () => void,
    zoom: number = 16.5
) {
    if (!map) return
    if (!Array.isArray(target)) return

    const currentCenter = map.getCenter() // Current map center

    const distance = getDistance(
        currentCenter.lat,
        currentCenter.lng,
        target[1],
        target[0],
        false
    ).value

    const distanceThreshold = 50 // For example, 50 km

    const rightPanelWidth =
        (eatery_state.eatery.gene ? 220 : 0) + (search.show ? 220 : 0)

    const padding:
        | number
        | maplibregl.RequireAtLeastOne<maplibregl.PaddingOptions>
        | undefined = {
        right: innerWidth > 768 ? rightPanelWidth : 0,
    }

    if (distance > distanceThreshold) {
        // Zoom out first if the distance is greater than the threshold
        map.flyTo({
            center: currentCenter,
            zoom: 8, // Lower zoom level to reduce tile loading
            duration: 500, // Quick zoom-out animation
            essential: true,
        })

        // Once zoom-out is complete, fly to the target
        const onZoomOutEnd = () => {
            map!.off('moveend', onZoomOutEnd)
            map!.flyTo({
                center: target,
                zoom: zoom,
                duration: 500,
                padding,
                essential: true,
            })

            const onFlyToEnd = () => {
                map!.off('moveend', onFlyToEnd)
                callback && callback()
            }

            map!.on('moveend', onFlyToEnd)
        }

        map.on('moveend', onZoomOutEnd)
    } else {
        // Fly directly to the target if the distance is within the threshold
        map.flyTo({
            center: target,
            zoom: zoom,
            essential: true,
            padding,
        })

        const onFlyToEnd = () => {
            map!.off('moveend', onFlyToEnd)
            callback && callback()
        }

        map.on('moveend', onFlyToEnd)
    }
}

createRoot(() => {
    onCleanup(() => {
        stopLiveLocationTracking()
    })
})

export {
    startLiveLocationTracking,
    location,
    setLocation,
    mapState,
    setMapState,
    type FILTERS as MAP_FILTERS,
}
